import React, { useState } from 'react';
import {
  VStack,
  Box,
  Text,
  Heading,
  Button,
  useColorModeValue,
  Badge,
  IconButton,
  Flex,
  AspectRatio,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Image
} from '@chakra-ui/react';
import {
  Images,
  Trash,
  ArrowsOut,
  DotsThreeVertical,
  Star,
  Image as ImageIcon,
  Video,
  Question,
  StarHalf
} from '@phosphor-icons/react';
import { useCreateSite } from '../../../context/CreateSiteContext';
import { motion, AnimatePresence } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import MediaUpload from '../../../components/MediaUpload';

const MotionBox = motion(Box);

// Media Item Component for displaying uploaded media
const MediaItem = ({ media, onDelete, onSetPrimary, isPrimary }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const bgColor = useColorModeValue(
    isPrimary ? 'rgba(193, 148, 52, 0.1)' : 'gray.50',
    isPrimary ? 'rgba(214, 173, 96, 0.1)' : 'gray.700'
  );
  const borderColor = useColorModeValue(
    isPrimary ? 'rgba(193, 148, 52, 0.3)' : 'gray.200',
    isPrimary ? 'rgba(214, 173, 96, 0.3)' : 'gray.600'
  );
  const textColor = useColorModeValue('text.default', 'text.default');
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');
  
  // Detect media type
  const isVideo = media?.url?.includes('video') || media?.contentType?.includes('video');

  return (
    <MotionBox
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
      bg={bgColor}
      borderColor={borderColor}
      position="relative"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      _before={isPrimary ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '4px',
        bg: accentColor,
        zIndex: 1
      } : undefined}
    >
      <AspectRatio ratio={4/3}>
        <Box position="relative">
          {/* Media Preview */}
          {isVideo ? (
            <Flex
              align="center"
              justify="center"
              bg="black"
              h="100%"
              w="100%"
            >
              <Box as="video" 
                objectFit="cover" 
                width="100%" 
                height="100%" 
                src={media.url}
              />
              <Box
                position="absolute"
                bg="blackAlpha.600"
                borderRadius="full"
                p={3}
              >
                <Video size={24} color="white" weight="fill" />
              </Box>
            </Flex>
          ) : (
            <Image
              src={media.url}
              alt={media.name || "Uploaded image"}
              objectFit="cover"
              width="100%"
              height="100%"
              fallback={
                <Flex align="center" justify="center" bg="gray.100" h="100%" w="100%">
                  <Question size={36} color={mutedColor} />
                </Flex>
              }
            />
          )}
          
          {/* Actions overlay */}
          <Flex
            position="absolute"
            top={0}
            right={0}
            p={2}
            zIndex={1}
          >
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                icon={<DotsThreeVertical />}
                variant="solid"
                colorScheme="blackAlpha"
                size="sm"
                aria-label="Media options"
              />
              <MenuList>
                <MenuItem
                  icon={<ArrowsOut size={16} />}
                  onClick={onOpen}
                >
                  View Larger
                </MenuItem>
                {!isPrimary && (
                  <MenuItem
                    icon={<Star size={16} />}
                    onClick={() => onSetPrimary(media.id)}
                  >
                    Set as Primary Image
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Trash size={16} />}
                  color="red.500"
                  onClick={() => onDelete(media.id)}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
          
          {/* Media type indicator */}
          <Badge
            position="absolute"
            bottom={2}
            left={2}
            colorScheme={isVideo ? "purple" : "blue"}
            variant="solid"
            size="sm"
          >
            <HStack spacing={1}>
              {isVideo ? <Video size={10} /> : <ImageIcon size={10} />}
              <Text fontSize="xs">{isVideo ? 'Video' : 'Photo'}</Text>
            </HStack>
          </Badge>
          
          {/* Primary indicator */}
          {isPrimary && (
            <Badge
              position="absolute"
              top={2}
              left={2}
              colorScheme="yellow"
              variant="solid"
              size="sm"
            >
              <HStack spacing={1}>
                <Star size={10} weight="fill" />
                <Text fontSize="xs">Primary</Text>
              </HStack>
            </Badge>
          )}
        </Box>
      </AspectRatio>
      
      {/* Caption */}
      {media.caption && (
        <Box p={2}>
          <Text fontSize="xs" noOfLines={1} color={textColor}>
            {media.caption}
          </Text>
        </Box>
      )}
      
      {/* Media preview modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{media.caption || "Media Preview"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isVideo ? (
              <Box as="video"
                controls
                width="100%"
                src={media.url}
              />
            ) : (
              <Image
                src={media.url}
                alt={media.caption || "Media preview"}
                width="100%"
                objectFit="contain"
              />
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={4} justify="space-between" width="100%">
              <Text fontSize="sm" color={mutedColor}>
                {media.name || "Uploaded media"}
              </Text>
              <Button onClick={onClose}>Close</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionBox>
  );
};


export const MediaStep = () => {
  const {
    siteData,
    setSiteData,
    validationErrors
  } = useCreateSite();
  
  const [showUploader, setShowUploader] = useState(false);
  const mediaList = siteData.media || [];
  const primaryMediaId = siteData.primaryMediaId;
  
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  const cardBg = useColorModeValue('bg.surface', 'bg.surface');
  
  const handleAddMedia = (newMediaItems) => {
    if (!newMediaItems || !Array.isArray(newMediaItems) || newMediaItems.length === 0) return;
    
    // Convert MediaUpload response format to match our CreateSite context format
    const formattedMediaItems = newMediaItems.map(item => ({
      id: item.id || uuidv4(), // Use existing ID or generate new one
      url: item.url,
      path: item.path,
      name: item.name,
      contentType: item.type,
      caption: item.caption || item.name,
      uploadedAt: item.uploadedAt || new Date().toISOString(),
      features: item.features || {},
      predictions: item.predictions || [],
      sublocationId: item.sublocationId || null,
      coordinates: item.coordinates || null,
      isMain: item.isMain || false
    }));
    
    const updatedMedia = [...mediaList, ...formattedMediaItems];
    
    // If this is the first media, make it the primary
    const updatedState = {
      ...siteData,
      media: updatedMedia
    };
    
    if (updatedMedia.length > 0 && !updatedState.primaryMediaId) {
      // Find a media item marked as main or use the first one
      const mainItem = updatedMedia.find(m => m.isMain) || updatedMedia[0];
      updatedState.primaryMediaId = mainItem.id;
      updatedState.mainImage = mainItem.url;
    }
    
    setSiteData(updatedState);
    setShowUploader(false);
  };
  
  const handleDeleteMedia = (mediaId) => {
    const updatedMedia = mediaList.filter(item => item.id !== mediaId);
    
    const updatedState = {
      ...siteData,
      media: updatedMedia
    };
    
    // If deleting the primary media, set a new one if available
    if (mediaId === primaryMediaId && updatedMedia.length > 0) {
      updatedState.primaryMediaId = updatedMedia[0].id;
      updatedState.mainImage = updatedMedia[0].url;
    } else if (mediaId === primaryMediaId) {
      // No media left, clear primary
      updatedState.primaryMediaId = null;
      updatedState.mainImage = null;
    }
    
    setSiteData(updatedState);
  };
  
  const handleSetPrimaryMedia = (mediaId) => {
    const mediaItem = mediaList.find(item => item.id === mediaId);
    if (!mediaItem) return;
    
    setSiteData({
      ...siteData,
      primaryMediaId: mediaId,
      mainImage: mediaItem.url
    });
  };
  
  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading as="h1" size="lg" mb={2} fontFamily="heading">
            Media Gallery
          </Heading>
          <Text color={mutedColor}>
            Add photos and videos of the megalithic site
          </Text>
        </Box>
        
        {/* Media Upload Component */}
        {showUploader ? (
          <Box mb={4}>
            <MediaUpload 
              onUploadComplete={handleAddMedia}
              siteId={null} // Use null to ensure files go to temp storage
              media={mediaList}
            />
            <Button 
              mt={4} 
              onClick={() => setShowUploader(false)} 
              variant="outline"
            >
              Done
            </Button>
          </Box>
        ) : (
          <Button
            leftIcon={<Images />}
            onClick={() => setShowUploader(true)}
            mb={4}
            colorScheme="yellow"
          >
            Upload Media
          </Button>
        )}
        
        {/* Media Grid */}
        <Box>
          {mediaList.length > 0 ? (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
              <AnimatePresence>
                {mediaList.map(media => (
                  <MediaItem
                    key={media.id}
                    media={media}
                    onDelete={handleDeleteMedia}
                    onSetPrimary={handleSetPrimaryMedia}
                    isPrimary={media.id === primaryMediaId}
                  />
                ))}
              </AnimatePresence>
            </SimpleGrid>
          ) : (
            <Box
              p={6}
              textAlign="center"
              borderWidth="1px"
              borderStyle="dashed"
              borderRadius="md"
              borderColor={useColorModeValue('gray.200', 'gray.600')}
            >
              <VStack spacing={3}>
                <Images size={24} opacity={0.5} />
                <Text color={mutedColor}>
                  No media uploaded yet
                </Text>
                <Text fontSize="sm" color={mutedColor}>
                  Upload photos and videos to showcase the site
                </Text>
              </VStack>
            </Box>
          )}
        </Box>
        
        {/* Primary Media Help */}
        {mediaList.length > 0 && (
          <Box mt={4} p={3} borderRadius="md" bg={cardBg}>
            <HStack>
              <StarHalf size={16} color={useColorModeValue('brand.primary', 'brand.primary')} />
              <Text fontSize="sm" color={mutedColor}>
                The primary image will be shown as the site's main image in search results
              </Text>
            </HStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
